import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingDown, Users, GraduationCap, BarChart3, AlertTriangle, CheckCircle, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';

const keyStats = [
  { value: "3.59%", label: "Harvard Class of 2028 acceptance rate", trend: "down", context: "From 3.6% in 2027" },
  { value: "3.68%", label: "Stanford acceptance rate", trend: "down", context: "Record low" },
  { value: "4.35%", label: "Yale acceptance rate", trend: "down", context: "From 4.6% in 2027" },
  { value: "67,232", label: "Applications to Harvard for 1,645 spots", trend: "up", context: "+5% from prior year" },
];

const trends = [
  {
    title: "Applications Are Surging — Acceptance Rates Are Not",
    body: "In the 2023-24 cycle, the Common App reported 7.1 million applications submitted — a 41% increase over pre-pandemic levels. Meanwhile, class sizes have barely changed. The math is brutal: more applicants, same number of seats.",
    icon: TrendingDown,
  },
  {
    title: "Test-Optional Hasn't Made It Easier",
    body: "After COVID, most top schools went test-optional. The result? Application volumes exploded because perceived barriers dropped. Students who do submit strong scores now have a meaningful advantage in the applicant pool.",
    icon: AlertTriangle,
  },
  {
    title: "Early Decision Rates Are 3-4x Higher",
    body: "At schools like UPenn, the ED acceptance rate is ~15% vs ~5% regular. At Dartmouth: 19% ED vs 4.7% overall. Schools fill 40-60% of their class through early rounds. Strategy around early applications is no longer optional — it's decisive.",
    icon: CheckCircle,
  },
  {
    title: "Demonstrated Interest Is Quietly Decisive",
    body: "Schools like Tulane, NYU, Northeastern, and Boston University track every interaction — campus visits, email opens, info session attendance. At some schools, demonstrated interest is weighted as heavily as essays.",
    icon: Users,
  },
  {
    title: "The Activity List Is More Competitive Than Ever",
    body: "Admissions officers report that the average applicant to a top-20 school now has 2-3 'leadership' positions, a passion project, research experience, and 200+ hours of community service. The floor has risen dramatically.",
    icon: BookOpen,
  },
  {
    title: "AI Is Changing Essay Evaluation",
    body: "Admissions offices are increasingly using AI detection tools to flag essays that feel generic or machine-written. Authentic voice, structural originality, and narrative specificity matter more than ever.",
    icon: BarChart3,
  },
];

const acceptanceData = [
  { school: "Harvard", rate: "3.59%", apps: "67,232", cls: "2028" },
  { school: "Stanford", rate: "3.68%", apps: "56,378", cls: "2028" },
  { school: "MIT", rate: "3.96%", apps: "28,232", cls: "2028" },
  { school: "Columbia", rate: "3.93%", apps: "60,551", cls: "2028" },
  { school: "Caltech", rate: "3.1%", apps: "16,626", cls: "2028" },
  { school: "Yale", rate: "4.35%", apps: "57,465", cls: "2028" },
  { school: "Brown", rate: "5.0%", apps: "51,302", cls: "2028" },
  { school: "UPenn", rate: "5.4%", apps: "59,463", cls: "2028" },
  { school: "Duke", rate: "5.0%", apps: "54,191", cls: "2028" },
  { school: "Princeton", rate: "5.73%", apps: "39,644", cls: "2028" },
  { school: "Dartmouth", rate: "6.24%", apps: "31,042", cls: "2028" },
  { school: "Vanderbilt", rate: "5.6%", apps: "47,174", cls: "2028" },
  { school: "Northwestern", rate: "6.84%", apps: "52,225", cls: "2028" },
  { school: "Johns Hopkins", rate: "6.5%", apps: "37,150", cls: "2028" },
  { school: "Rice", rate: "7.7%", apps: "31,049", cls: "2028" },
  { school: "Cornell", rate: "7.87%", apps: "67,380", cls: "2028" },
  { school: "Georgetown", rate: "11.4%", apps: "28,398", cls: "2028" },
  { school: "Emory", rate: "11.4%", apps: "33,534", cls: "2028" },
  { school: "Carnegie Mellon", rate: "11.2%", apps: "36,522", cls: "2028" },
  { school: "USC", rate: "9.9%", apps: "80,790", cls: "2028" },
  { school: "UCLA", rate: "8.6%", apps: "145,904", cls: "2028" },
  { school: "NYU", rate: "8.0%", apps: "120,000", cls: "2028" },
  { school: "UMich", rate: "17.7%", apps: "87,997", cls: "2028" },
  { school: "UVA", rate: "16.3%", apps: "56,439", cls: "2028" },
  { school: "Boston College", rate: "16.0%", apps: "40,474", cls: "2028" },
];

export default function AdmissionsFactsPage() {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? acceptanceData : acceptanceData.slice(0, 15);

  return (
    <div className="min-h-screen bg-white" data-testid="facts-page">
      <SEO title="College Admissions Facts & Data 2025" description="Current acceptance rates, application trends, and critical data for the top 25 universities. Updated for the Class of 2028-2029 cycle. Free resource from Ivy League Consulting." />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-16 bg-[#0F392B] py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">Updated for 2025-2026</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            College Admissions <span className="italic">by the Numbers</span>
          </h1>
          <p className="text-base text-white/70 max-w-2xl mx-auto mb-8">
            The data parents and students need to understand today's admissions landscape. No spin. No sales pitch. Just the numbers.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {keyStats.map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center" data-testid={`key-stat-${i}`}>
                <p className="text-2xl md:text-3xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                <p className="text-xs text-white/60 mt-1">{s.label}</p>
                <p className="text-[10px] text-[#D4AF37] mt-1">{s.context}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Acceptance Rates Table */}
      <section className="py-16 md:py-24" data-testid="rates-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Acceptance Rates — Top 25 Schools
          </h2>
          <p className="text-sm text-muted-foreground mb-8">Class of 2028 admissions data. Sorted by selectivity.</p>
          <div className="overflow-x-auto rounded-xl border border-[#E2E2E2]">
            <table className="w-full" data-testid="rates-table">
              <thead>
                <tr className="bg-[#F9F9F7] border-b border-[#E2E2E2]">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">#</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">School</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Acceptance Rate</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Applications</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Class</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((d, i) => (
                  <tr key={i} className="border-b border-[#E2E2E2] hover:bg-[#F9F9F7] transition-colors" data-testid={`rate-row-${i}`}>
                    <td className="py-3 px-4 text-xs text-muted-foreground">{i + 1}</td>
                    <td className="py-3 px-4 text-sm font-medium">{d.school}</td>
                    <td className="py-3 px-4">
                      <span className={`text-sm font-semibold ${parseFloat(d.rate) < 5 ? 'text-red-600' : parseFloat(d.rate) < 10 ? 'text-orange-600' : 'text-[#0F392B]'}`}>{d.rate}</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">{parseInt(d.apps.replace(/,/g, '')).toLocaleString()}</td>
                    <td className="py-3 px-4 text-xs text-muted-foreground">{d.cls}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!showAll && (
            <button onClick={() => setShowAll(true)} className="mt-4 text-sm text-[#0F392B] font-medium hover:underline" data-testid="show-all-btn">
              Show all 25 schools
            </button>
          )}
        </div>
      </section>

      {/* Portal CTA */}
      <section className="py-12 bg-[#0F392B]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-sm text-[#D4AF37] uppercase tracking-wide mb-2">Wondering Where You Stand?</p>
          <h3 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Try our free Admissions Calculator
          </h3>
          <p className="text-sm text-white/60 mb-6">Input your GPA and test scores. See your chances at every school on this list.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/calculator">
              <Button className="bg-white text-[#0F392B] hover:bg-white/90 h-11 px-6" data-testid="facts-calc-cta">
                Try the Calculator <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/sign-up">
              <Button variant="outline" className="h-11 px-6 border-white/25 text-white hover:bg-white/10">
                Access Portal — $99
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trends */}
      <section className="py-16 md:py-24" data-testid="trends-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            6 Trends Defining the 2025-2026 Admissions Cycle
          </h2>
          <p className="text-sm text-muted-foreground mb-10">What every parent and student needs to know right now.</p>
          <div className="space-y-6">
            {trends.map((t, i) => (
              <div key={i} className="flex items-start gap-4 p-6 rounded-xl bg-[#F9F9F7] border border-[#E2E2E2] hover:shadow-md transition-shadow" data-testid={`trend-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[#0F392B] flex items-center justify-center flex-shrink-0">
                  <t.icon className="h-5 w-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-base font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{t.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-[#F9F9F7] border-t border-[#E2E2E2]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-medium mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            The data is clear: <span className="italic">organization is the edge.</span>
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            With acceptance rates at historic lows, the margin for error is zero. The $99 Portal gives you the structure to present your best application — plus a 45-minute session with a former admissions officer.
          </p>
          <Link to="/sign-up">
            <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-12 px-8 text-base" data-testid="facts-bottom-cta">
              Access the Portal — $99 <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
